/**
 * Validazione età partecipanti per iscrizioni a squadre (es. danza per categoria).
 *
 * Regola fuori quota (danza):
 * - sotto la soglia minima (default 10 componenti) non è ammesso alcun fuori quota;
 * - alla soglia si attivano i fuori quota base (default 2);
 * - ogni "step" di componenti in più (default 5) si aggiunge 1 fuori quota.
 *
 * Esempio con i default: 6 componenti -> 0, 10 -> 2, 15 -> 3, 20 -> 4.
 */

const AGE_LABEL_REGEX = /\((\d+)\s*[-–]\s*(\d+)(?:\s*anni?)?\)/i;
export const DEFAULT_OUT_OF_QUOTA = 2;
export const DEFAULT_OUT_OF_QUOTA_RULE = Object.freeze({
  enabled: true,
  minMembers: 10,
  baseQuota: 2,
  stepSize: 5
});

const PERFORMANCE_SIZE_RULES = {
  solisti: { key: 'solisti', label: 'Solisti', min: 1, max: 1 },
  duo: { key: 'duo', label: 'Duo/Passo a due', min: 2, max: 2 },
  gruppi: { key: 'gruppi', label: 'Gruppi', min: 3, max: null }
};

function toNonNegativeInt(value, fallback, min = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < min) return fallback;
  return Math.floor(parsed);
}

/**
 * Normalizza i metadati età di un'opzione.
 */
export function normalizeOption(option) {
  if (typeof option === 'string') {
    const label = option.trim();
    const value = label;
    const match = label.match(AGE_LABEL_REGEX);
    if (match) {
      return {
        value,
        label,
        minAge: parseInt(match[1], 10),
        maxAge: parseInt(match[2], 10),
        maxOutOfQuota: DEFAULT_OUT_OF_QUOTA
      };
    }
    return { value, label, minAge: null, maxAge: null, maxOutOfQuota: 0 };
  }

  if (option && typeof option === 'object') {
    const label = (option.label || option.value || '').trim();
    const value = (option.value || option.label || '').trim();
    let minAge = option.minAge !== null && option.minAge !== undefined ? Number(option.minAge) : null;
    let maxAge = option.maxAge !== null && option.maxAge !== undefined ? Number(option.maxAge) : null;

    if (minAge === null || maxAge === null) {
      const match = label.match(AGE_LABEL_REGEX);
      if (match) {
        minAge = minAge ?? parseInt(match[1], 10);
        maxAge = maxAge ?? parseInt(match[2], 10);
      }
    }

    const hasRange = minAge !== null && maxAge !== null && !Number.isNaN(minAge) && !Number.isNaN(maxAge);
    const maxOutOfQuota = option.maxOutOfQuota !== null && option.maxOutOfQuota !== undefined
      ? Number(option.maxOutOfQuota)
      : (hasRange ? DEFAULT_OUT_OF_QUOTA : 0);

    return {
      value,
      label,
      minAge: hasRange ? minAge : null,
      maxAge: hasRange ? maxAge : null,
      maxOutOfQuota: Number.isNaN(maxOutOfQuota) ? 0 : maxOutOfQuota
    };
  }

  const str = String(option);
  return { value: str, label: str, minAge: null, maxAge: null, maxOutOfQuota: 0 };
}

/**
 * Estrae l'intervallo età da un'etichetta tipo "Predanza 3 (8-10 anni)".
 */
export function parseAgeRangeFromLabel(label) {
  const match = String(label || '').match(AGE_LABEL_REGEX);
  if (!match) return null;
  return {
    minAge: parseInt(match[1], 10),
    maxAge: parseInt(match[2], 10)
  };
}

/**
 * Aggiorna l'etichetta di un'opzione preservando i metadati.
 * Se la nuova etichetta contiene un intervallo di età, min/max vengono riallineati.
 */
export function updateOptionLabel(option, newLabel) {
  const normalized = normalizeOption(option ?? newLabel);
  const parsed = parseAgeRangeFromLabel(newLabel);

  return {
    ...normalized,
    label: newLabel,
    value: newLabel,
    ...(parsed ? { minAge: parsed.minAge, maxAge: parsed.maxAge } : {})
  };
}

export function getOptionValue(option) {
  return normalizeOption(option).value;
}

export function getOptionLabel(option) {
  return normalizeOption(option).label;
}

/**
 * Normalizza la regola fuori quota. Se la regola non è presente usa i default
 * (attiva per la danza).
 */
export function normalizeOutOfQuotaRule(rule, fallbackEnabled = true) {
  const source = rule && typeof rule === 'object' ? rule : {};
  const enabled = source.enabled === undefined ? fallbackEnabled : source.enabled !== false;

  return {
    enabled,
    minMembers: toNonNegativeInt(source.minMembers, DEFAULT_OUT_OF_QUOTA_RULE.minMembers, 1),
    baseQuota: toNonNegativeInt(source.baseQuota, DEFAULT_OUT_OF_QUOTA_RULE.baseQuota, 0),
    stepSize: toNonNegativeInt(source.stepSize, DEFAULT_OUT_OF_QUOTA_RULE.stepSize, 1)
  };
}

/**
 * Calcola quanti partecipanti fuori quota sono ammessi per un totale di componenti.
 */
export function computeOutOfQuotaLimit(rule, totalMembers) {
  const normalized = normalizeOutOfQuotaRule(rule);
  if (!normalized.enabled) return null;

  const total = Math.max(0, Math.floor(Number(totalMembers) || 0));
  if (total < normalized.minMembers) return 0;

  return normalized.baseQuota
    + Math.floor((total - normalized.minMembers) / normalized.stepSize);
}

export function computeAge(birthDate, referenceDate = new Date()) {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(ref.getTime())) return null;

  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

function isAgeInRange(age, minAge, maxAge) {
  return age >= minAge && age <= maxAge;
}

/**
 * Riconosce il tipo di performance dal campo "category" in modo tollerante
 * rispetto a maiuscole/minuscole e piccole modifiche dell'etichetta.
 */
export function getPerformanceType(formFields) {
  const raw = String(formFields?.category || '').trim().toLowerCase();
  if (!raw) return null;

  if (raw.startsWith('solist')) return 'solisti';
  if (raw.startsWith('duo') || raw.includes('passo a due') || raw.includes('passo a 2')) return 'duo';
  if (raw.startsWith('grupp') || raw.includes('gruppo')) return 'gruppi';
  return null;
}

export function getTeamSizeRule(formFields) {
  const key = getPerformanceType(formFields);
  return key ? PERFORMANCE_SIZE_RULES[key] : null;
}

/**
 * Trova le regole età attive in base ai campi iscrizione e al form della squadra.
 */
export function getActiveAgeRules(registrationFields, formFields, eventCategory) {
  if (!Array.isArray(registrationFields) || !formFields) return null;

  for (const field of registrationFields) {
    if (field.fieldType !== 'select') continue;

    const enforceRules = field.enforceAgeRules === true
      || (eventCategory === 'danza' && field.fieldName === 'ageBand');

    if (!enforceRules) continue;

    const selectedValue = formFields[field.fieldName];
    if (!selectedValue) continue;

    const options = field.options || [];
    for (const opt of options) {
      const normalized = normalizeOption(opt);
      if (normalized.value === selectedValue && normalized.minAge !== null && normalized.maxAge !== null) {
        const performanceType = getPerformanceType(formFields);
        const outOfQuotaRule = normalizeOutOfQuotaRule(
          field.outOfQuotaRule,
          eventCategory === 'danza'
        );

        let maxOutOfQuota = normalized.maxOutOfQuota;

        // Regole per tipo performance: valgono solo quando la regola automatica
        // è disattivata (altrimenti decide la formula sul numero di componenti).
        if (!outOfQuotaRule.enabled) {
          if (performanceType === 'solisti') {
            maxOutOfQuota = 0;
          } else if (performanceType === 'duo') {
            maxOutOfQuota = Math.min(1, maxOutOfQuota);
          }
        }

        return {
          fieldName: field.fieldName,
          fieldLabel: field.fieldLabel || field.fieldName,
          categoryLabel: normalized.label,
          minAge: normalized.minAge,
          maxAge: normalized.maxAge,
          maxOutOfQuota,
          outOfQuotaRule,
          performanceType
        };
      }
    }
  }

  return null;
}

function classifyMembers(members, rules, referenceDate) {
  let inQuota = 0;
  let outOfQuota = 0;
  const missingBirthDate = [];
  const outOfRangeMembers = [];

  for (const member of members) {
    const age = computeAge(member.birth_date, referenceDate);
    if (age === null || age === undefined) {
      missingBirthDate.push(member);
      continue;
    }

    if (isAgeInRange(age, rules.minAge, rules.maxAge)) {
      inQuota += 1;
    } else {
      outOfQuota += 1;
      outOfRangeMembers.push({ member, age });
    }
  }

  return { inQuota, outOfQuota, missingBirthDate, outOfRangeMembers };
}

/**
 * Numero massimo di partecipanti fuori fascia consentiti.
 * Se la regola automatica è attiva dipende dal numero totale di componenti,
 * altrimenti usa il valore configurato sull'opzione.
 */
export function getAllowedOutOfQuota(rules, totalMembers) {
  if (!rules) return 0;

  if (rules.outOfQuotaRule && rules.outOfQuotaRule.enabled) {
    return computeOutOfQuotaLimit(rules.outOfQuotaRule, totalMembers);
  }

  return Math.max(0, Number(rules.maxOutOfQuota) || 0);
}

/**
 * Valida il numero di componenti in base al tipo performance
 * (Solisti = 1, Duo = 2, Gruppi >= 3).
 */
export function validateTeamSize({ members, memberCount, formFields }) {
  const sizeRule = getTeamSizeRule(formFields);
  if (!sizeRule) return { valid: true, sizeRule: null };

  let count;
  if (memberCount !== undefined && memberCount !== null && Number.isFinite(Number(memberCount))) {
    count = Number(memberCount);
  } else {
    count = Array.isArray(members) ? members.length : 0;
  }

  if (count < sizeRule.min) {
    return {
      valid: false,
      sizeRule,
      message: `Per "${sizeRule.label}" servono almeno ${sizeRule.min} partecipanti (attuali: ${count}).`
    };
  }

  if (sizeRule.max !== null && sizeRule.max !== undefined && count > sizeRule.max) {
    return {
      valid: false,
      sizeRule,
      message: `Per "${sizeRule.label}" sono ammessi al massimo ${sizeRule.max} partecipanti (attuali: ${count}).`
    };
  }

  return { valid: true, sizeRule, count };
}

/**
 * Valida i membri di una squadra rispetto alla categoria selezionata.
 */
export function validateTeamMemberAges({
  members,
  memberCount,
  registrationFields,
  formFields,
  eventCategory,
  referenceDate
}) {
  const rules = getActiveAgeRules(registrationFields, formFields, eventCategory);
  if (!rules) {
    return { valid: true, rules: null };
  }

  const refDate = referenceDate || new Date();
  const totalMembers = memberCount !== undefined && memberCount !== null
    ? Number(memberCount)
    : members.length;
  const {
    inQuota,
    outOfQuota,
    missingBirthDate,
    outOfRangeMembers
  } = classifyMembers(members, rules, refDate);
  const allowedOutOfQuota = getAllowedOutOfQuota(rules, totalMembers);

  if (missingBirthDate.length > 0) {
    const names = missingBirthDate
      .map(m => `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Partecipante')
      .join(', ');
    return {
      valid: false,
      rules,
      inQuota,
      outOfQuota,
      allowedOutOfQuota,
      message: `Data di nascita mancante per: ${names}. Impossibile verificare l'età per la categoria "${rules.categoryLabel}".`
    };
  }

  if (outOfQuota > allowedOutOfQuota) {
    const extra = outOfRangeMembers
      .slice(allowedOutOfQuota)
      .map(({ member, age }) => `${member.first_name} ${member.last_name} (${age} anni)`)
      .join(', ');

    const ruleHint = rules.outOfQuotaRule && rules.outOfQuotaRule.enabled
      ? ` Con ${totalMembers} componenti la regola consente ${allowedOutOfQuota} fuori quota `
        + `(da ${rules.outOfQuotaRule.minMembers} componenti: ${rules.outOfQuotaRule.baseQuota}, `
        + `+1 ogni ${rules.outOfQuotaRule.stepSize} componenti aggiuntivi).`
      : '';

    return {
      valid: false,
      rules,
      inQuota,
      outOfQuota,
      allowedOutOfQuota,
      message: `Per "${rules.categoryLabel}" sono ammessi al massimo ${allowedOutOfQuota} partecipanti fuori fascia (${rules.minAge}-${rules.maxAge} anni).`
        + ruleHint
        + ` Attualmente ${outOfQuota} fuori fascia${extra ? `: ${extra}` : ''}.`
    };
  }

  return { valid: true, rules, inQuota, outOfQuota, allowedOutOfQuota };
}

/**
 * Valida l'intera iscrizione: numero componenti + vincoli di età.
 */
export function validateTeamRegistration(args) {
  const sizeValidation = validateTeamSize(args);
  if (!sizeValidation.valid) return sizeValidation;
  return { ...validateTeamMemberAges(args), sizeRule: sizeValidation.sizeRule };
}

/**
 * Verifica se aggiungere un membro rispetta ancora i vincoli di età.
 */
export function canAddMemberToTeam(member, currentMembers, rules, referenceDate) {
  if (!rules) return { allowed: true };

  const age = computeAge(member.birth_date, referenceDate);
  if (age === null || age === undefined) {
    return {
      allowed: false,
      reason: 'Data di nascita non disponibile: impossibile verificare l\'età.'
    };
  }

  if (isAgeInRange(age, rules.minAge, rules.maxAge)) {
    return { allowed: true, status: 'in_quota' };
  }

  const totalAfter = currentMembers.length + 1;
  const { outOfQuota } = classifyMembers(currentMembers, rules, referenceDate);
  const allowedOutOfQuota = getAllowedOutOfQuota(rules, totalAfter);

  if (outOfQuota >= allowedOutOfQuota) {
    if (rules.outOfQuotaRule && rules.outOfQuotaRule.enabled && totalAfter < rules.outOfQuotaRule.minMembers) {
      return {
        allowed: false,
        status: 'out_of_quota',
        allowedOutOfQuota,
        reason: `La formazione avrà ${totalAfter} componenti: con meno di ${rules.outOfQuotaRule.minMembers} componenti non è consentito alcun partecipante fuori fascia.`
      };
    }

    return {
      allowed: false,
      status: 'out_of_quota',
      allowedOutOfQuota,
      reason: `Limite di ${allowedOutOfQuota} partecipanti fuori fascia raggiunto per ${totalAfter} componenti (${rules.minAge}-${rules.maxAge} anni).`
    };
  }

  return { allowed: true, status: 'out_of_quota_slot', allowedOutOfQuota };
}

export function getMemberAgeStatus(member, rules, referenceDate) {
  if (!rules) return 'unknown';
  const age = computeAge(member.birth_date, referenceDate);
  if (age === null || age === undefined) return 'no_birth_date';
  if (isAgeInRange(age, rules.minAge, rules.maxAge)) return 'in_quota';
  return 'out_of_quota';
}

export function getEventReferenceDate(event) {
  if (!event) return new Date();
  const raw = event.eventDate || event.event_date;
  if (!raw) return new Date();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}
